/**
 * Flow Graph Router
 *
 * Builds a routing table from a FlowGraph and resolves phase transitions
 * at runtime. The coordinator calls followEdge(outputPin) and the router
 * returns the next phase based on the saved graph edges.
 *
 * Seamless phase variants (seamless_page_viewed, etc.) are normalized
 * to their non-seamless equivalents since the coordinator only uses
 * non-seamless phase names internally.
 */

import type { FlowGraph, FlowNode } from '@playcastdotio/Messaging';

type CoordinatorPhase = string;

const SEAMLESS_PREFIX = 'seamless_';

function normalizePhase(phase: string): CoordinatorPhase {
  if (phase.startsWith(SEAMLESS_PREFIX)) {
    return phase.slice(SEAMLESS_PREFIX.length);
  }
  return phase;
}

export class FlowGraphRouter {
  private edgeTable: Map<CoordinatorPhase, Map<string, CoordinatorPhase>>;
  private startPhase: CoordinatorPhase;

  constructor(graph: FlowGraph) {
    this.edgeTable = new Map();

    const nodeById = new Map<string, FlowNode>();
    for (const node of graph.nodes) {
      nodeById.set(node.id, node);
    }

    for (const edge of graph.edges) {
      const sourceNode = nodeById.get(edge.source);
      const targetNode = nodeById.get(edge.target);
      if (!sourceNode || !targetNode) continue;

      const sourcePhase = normalizePhase(sourceNode.phase);
      const targetPhase = normalizePhase(targetNode.phase);

      let pinMap = this.edgeTable.get(sourcePhase);
      if (!pinMap) {
        pinMap = new Map();
        this.edgeTable.set(sourcePhase, pinMap);
      }
      pinMap.set(edge.sourceHandle, targetPhase);
    }

    const sorted = [...graph.nodes].sort((a, b) => a.position.x - b.position.x);
    this.startPhase = sorted.length > 0
      ? normalizePhase(sorted[0].phase)
      : 'page_viewed';
  }

  getNextPhase(currentPhase: CoordinatorPhase, outputPin: string): CoordinatorPhase | null {
    return this.edgeTable.get(currentPhase)?.get(outputPin) ?? null;
  }

  getStartPhase(): CoordinatorPhase {
    return this.startPhase;
  }

  hasPhase(phase: CoordinatorPhase): boolean {
    return this.edgeTable.has(phase);
  }
}
